import React, { useState, useEffect } from "react";
import {
  MoreVertical,
  CirclePlus,
  Edit,
  Trash2,
  Filter,
  X,
} from "lucide-react";
import AddCategoryModal from "./AddCategoryModal";
import EditCategoryModal from "./EditCategoryModal";
import CategoryTypePopup from "./CategoryTypePopup";
import { API_BASE_URL } from "../../../config/api";
import { categoryTypeService } from "../../../services/categoryTypeService";
import axios from "axios";
import { handleImageError, getImageUrl } from "../../../utils/imageUtils";

const CategoryManagement = ({ searchQuery }) => {
  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [categoryTypes, setCategoryTypes] = useState([]);
  const [showCategoryTypePopup, setShowCategoryTypePopup] = useState(false);
  const [selectedCategoryTypeFilter, setSelectedCategoryTypeFilter] =
    useState("");

  useEffect(() => {
    fetchCategories();
    fetchCategoryTypes();
  }, []);

  useEffect(() => {
    if (searchQuery) {
      const filtered = categories.filter((category) =>
        category.categoryName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        category.categoryTypeName.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredCategories(filtered);
    } else if (selectedCategoryTypeFilter) {
      const filtered = categories.filter(
        (category) => category.categoryTypeId === selectedCategoryTypeFilter
      );
      setFilteredCategories(filtered);
    } else {
      setFilteredCategories(categories);
    }
  }, [searchQuery, selectedCategoryTypeFilter, categories]);

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/categories/allcategory`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setCategories(data);
      setFilteredCategories(data);
    } catch (error) {
      console.error("Error fetching categories:", error);
      alert("Failed to load categories. Please try again later.");
    }
  };

  const fetchCategoryTypes = async () => {
    try {
      const data = await categoryTypeService.getAllCategoryTypes();
      setCategoryTypes(data);
    } catch (error) {
      console.error("Failed to fetch category types:", error);
    }
  };

  const uploadImage = async (imageFile) => {
    try {
      console.log("Starting image upload...", imageFile);

      // If imageFile is already a URL, return it directly
      if (typeof imageFile === "string" && imageFile.startsWith("http")) {
        return imageFile;
      }

      // Validate file
      if (!imageFile || !imageFile.type.startsWith("image/")) {
        throw new Error("Please select a valid image file");
      }

      const formData = new FormData();
      formData.append("file", imageFile);

      // Use API_BASE_URL instead of hardcoded URL
      const uploadUrl = `/upload`;
      console.log("Uploading to:", uploadUrl);
      console.log("File being uploaded:", imageFile.name, imageFile.type);

      const response = await axios.post(uploadUrl, formData, {
        headers: {
          Authorization: "QuindlTokPATFileUpload2025#$$TerOiu$",
          "Content-Type": "multipart/form-data",
          Accept: "application/json",
        },
        withCredentials: true, // Add this to handle CORS
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          console.log("Upload progress:", percentCompleted, "%");
        },
      });

      console.log("Upload response:", response.data);

      if (!response.data || !response.data.filePath) {
        throw new Error("Invalid response from upload server");
      }

      return response.data.filePath;
    } catch (error) {
      console.error("Detailed upload error:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        code: error.code,
      });

      if (error.message === "Network Error") {
        throw new Error(
          "Unable to connect to the server. Please check your connection and try again."
        );
      }

      // More specific error messages based on the error type
      if (error.response?.status === 401) {
        throw new Error("Unauthorized: Invalid or missing authorization token");
      } else if (error.response?.status === 413) {
        throw new Error("File is too large");
      } else if (error.code === "ECONNREFUSED") {
        throw new Error("Unable to connect to upload server");
      }

      throw new Error(`Failed to upload image: ${error.message}`);
    }
  };

  const handleAddCategory = async (
    categoryName,
    image,
    categoryTypeId,
    categoryTypeName
  ) => {
    try {
      console.log("Starting category addition with:", {
        categoryName,
        categoryTypeId,
        categoryTypeName,
      });

      if (!image) {
        throw new Error("Please select an image");
      }

      // Upload image first
      console.log("Uploading image...");
      const imageUrl = await uploadImage(image);
      console.log("Image uploaded successfully:", imageUrl);

      // Create category
      console.log("Creating category with image URL:", imageUrl);
      const response = await fetch(`${API_BASE_URL}/categories/addcategory`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          categoryName,
          categoryTypeId,
          categoryTypeName,
          categotyImage: imageUrl,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to add category");
      }

      const result = await response.json();
      console.log("Category created successfully:", result);

      await fetchCategories();
      setShowAddModal(false);
    } catch (error) {
      console.error("Detailed error adding category:", error);
      alert(`Failed to add category: ${error.message}`);
    }
  };

  const handleDelete = async (categoryId) => {
    setCategoryToDelete(categoryId);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/categories/editcategory/${categoryToDelete}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            isDeleted: true,
          }),
          credentials: "include",
        }
      );

      if (!response.ok) {
        throw new Error("Failed to delete category");
      }

      // Remove the deleted category from the local state
      setCategories((prevCategories) =>
        prevCategories.filter((category) => category._id !== categoryToDelete)
      );

      setShowDeleteModal(false);
      setCategoryToDelete(null);
      setOpenMenuId(null);
    } catch (error) {
      console.error("Error deleting category:", error);
      alert("Failed to delete category. Please try again.");
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setNewCategoryName(category.categoryName);
    setIsEditing(true);
    setOpenMenuId(null);
  };

  const handleSaveEdit = async (
    categoryName,
    newImage,
    categoryTypeId,
    categoryTypeName
  ) => {
    try {
      let imageUrl = editingCategory.categotyImage;

      // Upload new image if provided
      if (newImage) {
        console.log("Uploading new image for edit...", newImage);
        try {
          imageUrl = await uploadImage(newImage);
          console.log("New image uploaded successfully:", imageUrl);
        } catch (uploadError) {
          console.error("Image upload failed:", uploadError);
          alert("Failed to upload new image. Please try again.");
          return;
        }
      } else {
        console.log("Using existing image:", imageUrl);
      }

      const response = await fetch(
        `${API_BASE_URL}/categories/editcategory/${editingCategory._id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            categoryName: categoryName,
            categoryTypeId: categoryTypeId,
            categoryTypeName: categoryTypeName,
            categotyImage: imageUrl,
          }),
          credentials: "include", // Add this for CORS
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to update category");
      }

      console.log("Category updated successfully with image:", imageUrl);
      await fetchCategories();
      setIsEditing(false);
      setEditingCategory(null);
    } catch (error) {
      console.error("Error updating category:", error);
      alert(error.message || "Failed to update category. Please try again.");
    }
  };

  const toggleMenu = (e, categoryId) => {
    e.stopPropagation();
    setOpenMenuId(openMenuId === categoryId ? null : categoryId);
  };

  const handleCategoryTypeFilterChange = (e) => {
    setSelectedCategoryTypeFilter(e.target.value);
  };

  const clearFilter = () => {
    setSelectedCategoryTypeFilter("");
  };

  const ActionMenu = ({ category }) => (
    <div className="relative">
      <button
        onClick={(e) => toggleMenu(e, category._id)}
        className="p-2 hover:bg-gray-100 rounded-full"
      >
        <MoreVertical size={18} className="text-gray-500" />
      </button>

      {openMenuId === category._id && (
        <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
          <div className="py-1">
            <button
              onClick={() => handleEdit(category)}
              className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full"
            >
              <Edit size={16} />
              Edit
            </button>
            <button
              onClick={() => handleDelete(category._id)}
              className="flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-gray-100 w-full"
            >
              <Trash2 size={16} />
              Delete
            </button>
          </div>
        </div>
      )}
    </div>
  );

  const DeleteConfirmationModal = ({ onClose, onConfirm }) => {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-sm">
          <div className="flex flex-col items-center">
            {/* Delete Icon Circle */}
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <Trash2 size={32} className="text-red-500" />
            </div>

            {/* Title */}
            <h2 className="text-xl font-bold mb-2">Delete Category</h2>

            {/* Message */}
            <p className="text-gray-600 text-center mb-6">
              Do you want to delete this category? This action can't be undone
            </p>

            {/* Buttons */}
            <div className="flex gap-4 w-full">
              <button
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={onConfirm}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderCategoryImage = (imageUrl) => {
    // Add authorization header for image requests
    return (
      <img
        src={getImageUrl(imageUrl)}
        alt="Category"
        className="w-full h-full object-cover"
        onError={handleImageError}
      />
    );
  };

  const handleAddCategoryType = async (newType) => {
    await fetchCategoryTypes();
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-8">
        <div className="flex items-center gap-4">
          <h1 className="text-xl font-semibold">Category Management</h1>
          <span className="text-gray-500 font-medium">
            ({filteredCategories.length} categories)
          </span>

          <div className="flex items-center gap-2 ml-4">
            <select
              value={selectedCategoryTypeFilter}
              onChange={handleCategoryTypeFilterChange}
              className="px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500 text-sm"
            >
              <option value="">All Category Types</option>
              {categoryTypes.map((type) => (
                <option key={type._id} value={type._id}>
                  {type.typeName}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={() => setShowAddModal(true)}
            className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-md flex items-center gap-2"
          >
            <CirclePlus />
            Add Category
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="grid grid-cols-12 gap-4 p-4 border-b">
          <div className="col-span-2">
            <h3 className="text-sm font-medium text-gray-500">Product Image</h3>
          </div>
          <div className="col-span-4">
            <h3 className="text-sm font-medium text-gray-500">Category</h3>
          </div>
          <div className="col-span-4">
            <h3 className="text-sm font-medium text-gray-500">Category Type</h3>
          </div>
          <div className="col-span-2">
            <h3 className="text-sm font-medium text-gray-500">Action</h3>
          </div>
        </div>

        {filteredCategories.length > 0 ? (
          filteredCategories.map((category) => (
            <div
              key={category._id}
              className="grid grid-cols-12 gap-4 p-4 border-b hover:bg-gray-50"
            >
              <div className="col-span-2">
                <div className="w-16 h-16 overflow-hidden rounded">
                  {renderCategoryImage(category.categotyImage)}
                </div>
              </div>
              <div className="col-span-4 flex items-center">
                <span className="text-sm">{category.categoryName}</span>
              </div>
              <div className="col-span-4 flex items-center">
                <span className="text-sm capitalize">
                  {category.categoryTypeName}
                </span>
              </div>
              <div className="col-span-2 flex items-center justify-end">
                <ActionMenu category={category} />
              </div>
            </div>
          ))
        ) : (
          <div className="p-8 text-center text-gray-500">
            No categories found with the selected filter. Try a different filter
            or add a new category.
          </div>
        )}
      </div>

      {showAddModal && (
        <AddCategoryModal
          onClose={() => setShowAddModal(false)}
          onAdd={handleAddCategory}
        />
      )}

      {isEditing && (
        <EditCategoryModal
          category={editingCategory}
          onClose={() => {
            setIsEditing(false);
            setEditingCategory(null);
          }}
          onSave={handleSaveEdit}
        />
      )}

      {showDeleteModal && (
        <DeleteConfirmationModal
          onClose={() => {
            setShowDeleteModal(false);
            setCategoryToDelete(null);
          }}
          onConfirm={confirmDelete}
        />
      )}

      {/* Category Type Popup */}
      {showCategoryTypePopup && (
        <CategoryTypePopup
          onClose={() => setShowCategoryTypePopup(false)}
          onAdd={handleAddCategoryType}
        />
      )}
    </div>
  );
};

export default CategoryManagement;
